import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { isAdminAccessAllowed } from "@/lib/adminAccess";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Sign in | Coyote Creek Hat Co." },
      { name: "description", content: "Sign in to your Coyote Creek Hat Co. account." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/login" }],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [hostChecked, setHostChecked] = useState(false);
  const [hostAllowed, setHostAllowed] = useState(false);

  useEffect(() => {
    setHostAllowed(isAdminAccessAllowed());
    setHostChecked(true);
  }, []);

  useEffect(() => {
    if (hostChecked && hostAllowed && session) navigate({ to: "/admin", replace: true });
  }, [hostChecked, hostAllowed, session, navigate]);

  if (!hostChecked) return null;
  if (!hostAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-heading text-5xl text-primary">404</h1>
          <p className="mt-2 text-muted-foreground">Page not found.</p>
        </div>
      </div>
    );
  }


  const handleEmail = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/admin` },
        });
        if (error) throw error;
        toast.success("Check your email to confirm your account.");
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setBusy(true);
    const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin + "/admin" });
    if (result.error) {
      toast.error(result.error.message ?? "Google sign-in failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6 py-24">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <h1 className="font-heading text-3xl uppercase tracking-[0.15em] text-primary text-center">
          {mode === "signin" ? "Sign In" : "Create Account"}
        </h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">Admin access only</p>

        <button
          onClick={handleGoogle}
          disabled={busy}
          className="mt-6 w-full rounded-lg border border-border bg-background px-4 py-3 font-ui text-sm font-semibold uppercase tracking-widest text-primary hover:bg-muted disabled:opacity-50"
        >
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs uppercase tracking-widest text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={handleEmail} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-primary focus:outline-none focus:ring-2 focus:ring-blaze"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1">Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-primary focus:outline-none focus:ring-2 focus:ring-blaze"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full btn-cc-accent tracking-widest"
          >
            {busy ? "Please wait..." : mode === "signin" ? "Sign In" : "Sign Up"}
          </button>
        </form>

        <div className="mt-6 text-center text-sm">
          <button
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
            className="text-muted-foreground hover:text-primary underline"
          >
            {mode === "signin" ? "Need an account? Sign up" : "Have an account? Sign in"}
          </button>
        </div>
        <div className="mt-4 text-center">
          <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-primary">
            ← Back to site
          </Link>
        </div>
      </div>
    </div>
  );
}
